import { classByType, NotificationClass, NotificationType } from "./common"
import { Notification } from "./Notification"
import { NotificationBugWatermark } from "./NotificationBugWatermark"
import { NotificationCalendar } from "./NotificationCalendar"
import { NotificationComment } from "./NotificationComment"
import { NotificationEpic } from "./NotificationEpic"
import { NotificationGoal } from "./NotificationGoal"
import { IdeaActivityEventType, NotificationIdeaActivity } from "./NotificationIdeaActivity"
import { NotificationItem } from "./NotificationItem"
import { NotificationItemConvertedToEpic } from "./NotificationItemConvertedToEpic"
import { NotificationItemDeleted } from "./NotificationItemDeleted"
import { NotificationMilestone } from "./NotificationMilestone"
import { NotificationPlatformLoadMetrics } from "./NotificationPlatformLoadMetrics"
import { NotificationProjectAddition } from "./NotificationProjectAddition"
import { NotificationRetrospective } from "./NotificationRetrospective"
import { NotificationStoryPoker } from "./NotificationStoryPoker"
import { NotificationTask } from "./NotificationTask"
import { NotificationTeamAddition } from "./NotificationTeamAddition"
import { NotificationTimeOffImpact } from "./NotificationTimeOffImpact"
import { NotificationTimesheet } from "./NotificationTimesheet"
import { NotificationTodo } from "./NotificationTodo"

const itemNotificationChangesMarkers = [
  "ownerChanged",
  "storyPointsChanged",
  "descriptionChanged",
  "tasksChanged",
  "severityLevelChanged",
  "statusChanged",
  "teamChanged",
  "addedOrRemovedFromActiveSprint",
  "typeChanged",
  "hasNextTaskToWork",
  "demoFileChanged"
]

const severityItemTypes = ["BUG", "SUPPORT_CASE", "INCIDENT"]

export class NotificationInit {
  static generateNotificationType(changedField) {
    if (changedField === "hasNextTaskToWork") {
      return "NEXT_TASK_TO_WORK"
    }
    return changedField.includes("Changed")
      ? changedField.replace("Changed", "").toUpperCase()
      : "ACTIVE_SPRINT_ADDED"
  }

  static generateItemNotificationType(changedField) {
    if (changedField === "demoFileChanged") {
      return NotificationType.ITEM_DEMO_VIDEO
    }
    return `ITEM_UPDATE_${NotificationInit.generateNotificationType(changedField)}`
  }

  static isDemoVideoAdded(content) {
    const hasDemoAttachment = !!content?.demoAttachmentsMetadata?.attachments?.length
    const hasDemoVideo = !!(content?.demoVideoUrl || hasDemoAttachment)
    const hadDemoVideo = !!(
      content?.oldItem?.demoFile?.demoVideoUrl || content?.oldItem?.demoFile?.demoFileMetadataId
    )

    return hasDemoVideo && !hadDemoVideo
  }

  static parseItemNotification(props) {
    const content = props.content
    const result: Notification[] = []
    const fieldsKeys = Object.keys(content)
      .filter(field => itemNotificationChangesMarkers.includes(field))
      .filter(field => content[field] === true)
      .filter(field => field !== "demoFileChanged" || NotificationInit.isDemoVideoAdded(content))
      .filter(
        field =>
          field !== "severityLevelChanged" || severityItemTypes.includes(content.item?.itemType)
      )

    fieldsKeys.forEach(key => {
      result.push(
        new NotificationItem({
          ...props,
          type: NotificationInit.generateItemNotificationType(key)
        })
      )
    })
    return result
  }

  static parseItemCreatedNotification(props) {
    const content = props.content
    return content?.addedOrRemovedFromActiveSprint
      ? [
          new NotificationItem({
            ...props,
            type: "ITEM_UPDATE_ACTIVE_SPRINT_ADDED"
          })
        ]
      : []
  }

  /** Of all UPDATED events only a Description change has a card, the rest stay unrendered. */
  static parseIdeaActivityNotification(props: NotificationIdeaActivity) {
    return props.displayEvents
      .filter(
        event =>
          event.type !== IdeaActivityEventType.UPDATED || !!event.structuredDescriptionChanged
      )
      .map(
        event =>
          new NotificationIdeaActivity({
            ...props,
            type:
              event.type === IdeaActivityEventType.UPDATED
                ? NotificationType.IDEA_ACTIVITY_UPDATE_DESCRIPTION
                : `IDEA_ACTIVITY_${event.type}`,
            selectedEvent: event
          })
      )
  }

  static parseItemNotificationTasksUpdate(props) {
    const result: Notification[] = []
    props.content.tasks.forEach(task => {
      props["task"] = task
      result.push(
        new NotificationTask({
          ...props,
          type: "UPDATE_TASK"
        })
      )
    })

    return result
  }

  static create(props) {
    switch (classByType[props.type]) {
      case NotificationClass.NOTIFICATION_TODO:
        return new NotificationTodo(props)
      case NotificationClass.NOTIFICATION_ITEM:
        return new NotificationItem(props)
      case NotificationClass.NOTIFICATION_TASK:
        return new NotificationTask(props)
      case NotificationClass.NOTIFICATION_COMMENTS:
        return new NotificationComment(props)
      case NotificationClass.REPLY_COMMENT:
        return new NotificationComment(props)
      case NotificationClass.REACTION_ADDED_TO_COMMENT:
        return new NotificationComment(props)
      case NotificationClass.NOTIFICATION_BUG_WATERMARK:
        return new NotificationBugWatermark(props)
      case NotificationClass.NOTIFICATION_STORY_POKER:
        return new NotificationStoryPoker(props)
      case NotificationClass.NOTIFICATION_SPRINT_RETROSPECTIVE:
        return new NotificationRetrospective(props)
      case NotificationClass.NOTIFICATION_MILESTONE:
        return new NotificationMilestone(props)
      case NotificationClass.NOTIFICATION_PLATFORM_LOAD_METRICS:
        return new NotificationPlatformLoadMetrics(props)
      case NotificationClass.NOTIFICATION_GOAL:
        return new NotificationGoal(props)
      case NotificationClass.NOTIFICATION_PROJECT_ADDITION:
        return new NotificationProjectAddition(props)
      case NotificationClass.NOTIFICATION_TEAM_ADDITION:
        return new NotificationTeamAddition(props)
      case NotificationClass.NOTIFICATION_TIMESHEET:
        return new NotificationTimesheet(props)
      case NotificationClass.ITEM_DELETED:
        return new NotificationItemDeleted(props)
      case NotificationClass.ITEM_CONVERTED_TO_EPIC:
        return new NotificationItemConvertedToEpic(props)
      case NotificationClass.NOTIFICATION_EPIC_COMMENTS:
        return new NotificationComment(props)
      case NotificationClass.NOTIFICATION_IDEA_COMMENTS:
        return new NotificationComment(props)
      case NotificationClass.NOTIFICATION_EPIC:
        return new NotificationEpic(props)
      case NotificationClass.NOTIFICATION_IDEA_ACTIVITY:
        return new NotificationIdeaActivity(props)
      case NotificationClass.NEW_CURRENT_GOAL:
        return new NotificationGoal(props)
      case NotificationClass.NOTIFICATION_CALENDAR:
        return new NotificationCalendar(props)
      case NotificationClass.NOTIFICATION_TIME_OFF_IMPACT:
        return new NotificationTimeOffImpact(props)
      default:
        return new Notification(props)
    }
  }
}
